import Feature from "src/js/stores/user/Feature";
import { ConstraintsCategory } from "../../Constraints/types";
import {
  ConstraintsLayer,
  ConstraintsLayerType,
  ProtectedLandSummaryLayerType,
  SimplifyProfile,
} from "../layer_types/ConstraintsLayerType";
import { FloodingLayerType } from "../layer_types/FloodingLayerType";
import { Layer, LayerGroup } from "../types";

export const SIGNIFICANT_CONSTRAINTS: Layer = {
  id: ConstraintsCategory.POLICY,
  layerType: ConstraintsLayerType,
  title: "Significant constraints",
  group: LayerGroup.POLICY,
  icon: "lt-shield-line",
  description: "Could limit the type of development that can be done, or restrict use",
  toggleable: true,
  requiredFeatureAccess: [Feature.planningPolicyLayer],
  layerConfig: {
    rootCategory: ConstraintsCategory.POLICY,
    layerRoots: [
      "protected_areas_outer",
      "heritage_land_listed_buildings_outer",
      "flood_zone_2_env_agency",
      "flood_zone_3_env_agency",
      "conservation_areas_outer",
      "safe_guarding_outer",
      "employment_area_outer",
      "settlement_boundaries_outer",
      "tree_preservation_orders",
      "protected_views_outer",
      "green_belt_outer",
      "nutrient_neutrality",
      "water_neutrality",
      "national_landscape",
      "open_land_and_green_space",
      "very_low_risk_of_flooding_from_rivers_and_seas",
      "low_risk_of_flooding_from_rivers_and_seas",
      "medium_risk_of_flooding_from_rivers_and_seas",
      "high_risk_of_flooding_from_rivers_and_seas",
      "low_risk_of_flooding_from_surface_water",
      "medium_risk_of_flooding_from_surface_water",
      "high_risk_of_flooding_from_surface_water",
      "low_rofsw_climate_change",
      "medium_rofsw_climate_change",
      "high_rofsw_climate_change",
      "rights_of_way",
      "article_4_direction",
      "development_risk",
      "site_characterisation",
    ],
    simplifyProfile: SimplifyProfile.HIGH,
    categorySchema: [
      { key: "site_of_special_scientific_interest_(sssi)", defaultValue: false },
      { key: "national_nature_reserve", defaultValue: false },
      { key: "ramsar_site", defaultValue: false },
      { key: "special_area_of_conservation", defaultValue: false },
      { key: "biosphere_reserve", defaultValue: false },
      { key: "ancient_woodland", defaultValue: false },
      { key: "heritage_coasts", defaultValue: false },
      { key: "special_protection_area", defaultValue: false },
      { key: "common_land", defaultValue: false },
      { key: "local_nature_reserve", defaultValue: false },
      { key: "environmental_constraint", defaultValue: false },
      { key: "general_constraint", defaultValue: false },
      { key: "national_landscape", defaultValue: false },
      { key: "heritage_land_listed_buildings_outer", defaultValue: false },
      { key: "flood_zone_2_env_agency", defaultValue: false },
      { key: "flood_zone_3_env_agency", defaultValue: false },
      { key: "very_low_risk_of_flooding_from_rivers_and_seas", defaultValue: false },
      { key: "low_risk_of_flooding_from_rivers_and_seas", defaultValue: false },
      { key: "medium_risk_of_flooding_from_rivers_and_seas", defaultValue: false },
      { key: "high_risk_of_flooding_from_rivers_and_seas", defaultValue: false },
      { key: "low_risk_of_flooding_from_surface_water", defaultValue: false },
      { key: "medium_risk_of_flooding_from_surface_water", defaultValue: false },
      { key: "high_risk_of_flooding_from_surface_water", defaultValue: false },
      { key: "low_rofsw_climate_change", defaultValue: false },
      { key: "medium_rofsw_climate_change", defaultValue: false },
      { key: "high_rofsw_climate_change", defaultValue: false },
      { key: "conservation_areas_outer", defaultValue: false },
      { key: "employment_area_outer", defaultValue: false },
      { key: "settlement_boundaries_outer", defaultValue: false },
      { key: "green_belt_outer", defaultValue: false },
      { key: "nutrient_neutrality", defaultValue: false },
      { key: "water_neutrality_offsetting_allowed", defaultValue: false },
      { key: "water_neutrality_no_offsetting", defaultValue: false },
      { key: "protected_views_outer", defaultValue: false },
      { key: "safe_guarding_outer", defaultValue: false },
      { key: "metropolitan_open_land_outer", defaultValue: false },
      { key: "open_space_outer", defaultValue: false },
      { key: "article_4_ma", defaultValue: false },
      { key: "article_4_hmo", defaultValue: false },
      { key: "article_4_other", defaultValue: false },
      { key: "article_4_unknown", defaultValue: false },
      { key: "rights_of_way", defaultValue: false },
      { key: "tree_preservation_orders", defaultValue: false },
      { key: "development_risk", defaultValue: false },
      { key: "site_characterisation", defaultValue: false },
    ],
    forcedRasterCategories: [
      "low_risk_of_flooding_from_surface_water",
      "medium_risk_of_flooding_from_surface_water",
      "high_risk_of_flooding_from_surface_water",
      "low_rofsw_climate_change",
      "medium_rofsw_climate_change",
      "high_rofsw_climate_change",
    ],
    statusSchema: ["adopted", "draft"],
  },
  zoomConfig: {
    minVisualisationZoom: 10,
  },
  tags: ["rofsw"],
};

export const SIGNIFICANT_CONSTRAINTS_POLICIES: Layer = {
  id: ConstraintsCategory.POLICY,
  layerType: ConstraintsLayerType,
  title: "Policy Constraints",
  description: "Could limit the type of development that can be done, or restrict use",
  toggleable: true,
  requiredFeatureAccess: [Feature.planningPolicyLayer],
  layerConfig: {
    rootCategory: ConstraintsCategory.POLICY,
    layerRoots: [
      "site_of_special_scientific_interest_(sssi)",
      "national_nature_reserve",
      "ramsar_site",
      "special_area_of_conservation",
      "biosphere_reserve",
      "ancient_woodland",
      "heritage_coasts",
      "special_protection_area",
      "common_land",
      "local_nature_reserve",
      "environmental_constraint",
      "general_constraint",
      "national_landscape",
      "heritage_land_listed_buildings_outer",
      "conservation_areas_outer",
      "employment_area_outer",
      "settlement_boundaries_outer",
      "green_belt_outer",
      "protected_views_outer",
      "safe_guarding_outer",
      "open_land_and_green_space",
      "article_4_direction",
      "rights_of_way",
      "tree_preservation_orders",
      "development_risk",
      "site_characterisation",
    ],
    categorySchema: [
      { key: "site_of_special_scientific_interest_(sssi)", defaultValue: false },
      { key: "national_nature_reserve", defaultValue: false },
      { key: "ramsar_site", defaultValue: false },
      { key: "special_area_of_conservation", defaultValue: false },
      { key: "biosphere_reserve", defaultValue: false },
      { key: "ancient_woodland", defaultValue: false },
      { key: "heritage_coasts", defaultValue: false },
      { key: "special_protection_area", defaultValue: false },
      { key: "common_land", defaultValue: false },
      { key: "local_nature_reserve", defaultValue: false },
      { key: "environmental_constraint", defaultValue: false },
      { key: "general_constraint", defaultValue: false },
      { key: "national_landscape" },
      { key: "heritage_land_listed_buildings_outer" },
      { key: "conservation_areas_outer" },
      { key: "employment_area_outer" },
      { key: "settlement_boundaries_outer" },
      { key: "green_belt_outer" },
      { key: "protected_views_outer" },
      { key: "safe_guarding_outer" },
      { key: "metropolitan_open_land_outer" },
      { key: "open_space_outer" },
      { key: "article_4_ma" },
      { key: "article_4_hmo" },
      { key: "article_4_other" },
      { key: "article_4_unknown" },
      { key: "rights_of_way" },
      { key: "tree_preservation_orders" },
      { key: "development_risk" },
      { key: "site_characterisation" },
    ],
    statusSchema: ["adopted", "draft"],
  },
};

export const SIGNIFICANT_CONSTRAINTS_FLOODING: Layer = {
  id: `${ConstraintsCategory.POLICY}--flooding`,
  layerType: FloodingLayerType,
  title: "Flooding",
  description: "Could limit the type of development that can be done, or restrict use",
  toggleable: true,
  requiredFeatureAccess: [Feature.planningPolicyLayer],
  layerConfig: {
    rootCategory: ConstraintsCategory.POLICY,
    layerRoots: [
      "flood_zones_outer",
      "risk_of_flooding_from_rivers_and_seas",
      "risk_of_flooding_from_surface_water",
      "rofsw_climate_change",
    ],
    categorySchema: [
      { key: "flood_zones_outer" },
      { key: "very_low_risk_of_flooding_from_rivers_and_seas" },
      { key: "low_risk_of_flooding_from_rivers_and_seas" },
      { key: "medium_risk_of_flooding_from_rivers_and_seas" },
      { key: "high_risk_of_flooding_from_rivers_and_seas" },
      { key: "low_risk_of_flooding_from_surface_water" },
      { key: "medium_risk_of_flooding_from_surface_water" },
      { key: "high_risk_of_flooding_from_surface_water" },
      { key: "high_rofsw_climate_change" },
      { key: "medium_rofsw_climate_change" },
      { key: "low_rofsw_climate_change" },
    ],
    statusSchema: ["adopted", "draft"],
  },
};

export const SIGNIFICANT_CONSTRAINTS_WATER_AND_NUTRIENT_NEUTRALITY: Layer = {
  id: `${ConstraintsCategory.POLICY}--water-and-nutrient-neutrality`,
  layerType: ConstraintsLayerType,
  title: "Water and Nutrient Neutrality",
  description: "Could limit the type of development that can be done, or restrict use",
  toggleable: true,
  requiredFeatureAccess: [Feature.planningPolicyLayer],
  layerConfig: {
    rootCategory: ConstraintsCategory.POLICY,
    layerRoots: ["nutrient_neutrality", "water_neutrality"],
    categorySchema: [
      { key: "water_neutrality_offsetting_allowed" },
      { key: "water_neutrality_no_offsetting" },
      { key: "nutrient_neutrality" },
    ],
    statusSchema: ["adopted", "draft"],
  },
};

function createConstraintsLayer(
  layer: Pick<ConstraintsLayer, "title" | "description" | "layerConfig" | "id">
): ConstraintsLayer {
  return {
    layerType: ConstraintsLayerType,
    toggleable: true,
    requiredFeatureAccess: [Feature.planningPolicyLayer],
    ...layer,
  };
}

/** Provide user with better breakdown of policy data in site report. */
export const UK_POLICY_SITE_REPORT_LAYERS: Layer[] = [
  createConstraintsLayer({
    id: "protected_areas",
    title: "Protected Areas",
    description: "Protected Areas",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [{ key: "protected_areas_outer" }, { key: "national_landscape" }],
      statusSchema: ["adopted", "draft"],
    },
  }),
  createConstraintsLayer({
    id: "heritage_and_conservation",
    title: "Heritage and Conservation",
    description: "Heritage and Conservation",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [
        { key: "heritage_land_listed_buildings_outer" },
        { key: "conservation_areas_outer" },
        { key: "protected_views_outer" },
      ],
      statusSchema: ["adopted", "draft"],
    },
  }),
  createConstraintsLayer({
    id: "green_belt_open_space",
    title: "Green Belt, Open Space, and MOL",
    description: "Green Belt, Open Space, and MOL",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [
        { key: "green_belt_outer" },
        { key: "metropolitan_open_land_outer" },
        { key: "open_land_and_green_space" },
      ],
      statusSchema: ["adopted", "draft"],
    },
  }),
  createConstraintsLayer({
    id: "nutrient_and_water_neutrality",
    title: "Nutrient and Water Neutrality",
    description: "Nutrient and Water Neutrality",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [
        { key: "nutrient_neutrality" },
        { key: "water_neutrality" },
        { key: "water_neutrality_offsetting_allowed" },
        { key: "water_neutrality_no_offsetting" },
      ],
      statusSchema: ["adopted", "draft"],
    },
  }),
  createConstraintsLayer({
    id: "employment_land_use",
    title: "Employment Land Use",
    description: "Employment Land Use",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [{ key: "employment_area_outer" }],
      statusSchema: ["adopted", "draft"],
    },
  }),
  createConstraintsLayer({
    id: "settlement_boundaries",
    title: "Settlement Boundaries",
    description: "Settlement Boundaries",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [{ key: "settlement_boundaries_outer" }],
      statusSchema: ["adopted", "draft"],
    },
  }),
  createConstraintsLayer({
    id: "safeguarding",
    title: "Safeguarding",
    description: "Safeguarding",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [{ key: "safe_guarding_outer" }],
      statusSchema: ["adopted", "draft"],
    },
  }),
  createConstraintsLayer({
    id: "tree_preservation_orders",
    title: "Tree Preservation Orders",
    description: "Tree Preservation Orders",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [{ key: "tree_preservation_orders" }],
      statusSchema: ["adopted", "draft"],
    },
  }),
  createConstraintsLayer({
    id: "public_access",
    title: "Public Access",
    description: "Public Access",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [{ key: "rights_of_way" }],
      statusSchema: ["adopted", "draft"],
    },
  }),
];

/** Provide user with better breakdown of environmental data in site report. */
export const UK_ENVIRONMENTAL_SITE_REPORT_LAYERS: Layer[] = [
  createConstraintsLayer({
    id: "flood_zones",
    title: "Flood Zones",
    description: "Flood Zones",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [{ key: "flood_zones_outer" }],
      statusSchema: ["adopted", "draft"],
    },
  }),
  createConstraintsLayer({
    id: "flood_risk",
    title: "Flood Risk",
    description: "Flood Risk",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [
        { key: "very_low_risk_of_flooding_from_rivers_and_seas" },
        { key: "low_risk_of_flooding_from_rivers_and_seas" },
        { key: "medium_risk_of_flooding_from_rivers_and_seas" },
        { key: "high_risk_of_flooding_from_rivers_and_seas" },
      ],
      statusSchema: ["adopted", "draft"],
    },
  }),
  createConstraintsLayer({
    id: "risk_of_flooding_from_surface_water",
    title: "Surface Water Flood Risk",
    description: "Surface Water Flood Risk",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [
        { key: "low_risk_of_flooding_from_surface_water" },
        { key: "medium_risk_of_flooding_from_surface_water" },
        { key: "high_risk_of_flooding_from_surface_water" },
      ],
      statusSchema: ["adopted", "draft"],
    },
  }),
  createConstraintsLayer({
    id: "risk_of_flooding_from_surface_water_climate_change",
    title: "Surface Water Future Flood Risk",
    description: "Surface Water Future Flood Risk",
    layerConfig: {
      rootCategory: ConstraintsCategory.POLICY,
      categorySchema: [
        { key: "low_rofsw_climate_change" },
        { key: "medium_rofsw_climate_change" },
        { key: "high_rofsw_climate_change" },
      ],
      statusSchema: ["adopted", "draft"],
    },
  }),
];

export const PROTECTED_LAND_SUMMARY_LAYER: Layer = {
  id: "PROTECTED_LAND_SUMMARY",
  layerType: ProtectedLandSummaryLayerType,
  title: "Protected land",
  description: "Protected land",
  // TODO - currently piggy backing off insightsSummary feature as it's
  // availalbe from free and above. Should create a new feature that is specific
  // to this tier of insight summaries.
  requiredFeatureAccess: [Feature.insightsSummary],
  toggleable: true,
  hideByDefault: true,
  excludeFromReport: true,
  hideMapLayer: true,
};
